import { Either, Maybe } from 'monet';

/**
 * @description Настройки схемы расчета ЗП Администратора.
 * @param {string} name                Имя администратора.
 * @param {string} hourlyRate          Часовая ставка администратора.
 * @param {string} role                Роль администратора.
 * @param {string} selectedSalaryType  Выбранный тип заработной платы администратора.
 * @constructor
 */
export function AdminPayrollSettings(
  name = '',
  hourlyRate = '0',
  role = '',
  selectedSalaryType = '',
) {
  this.toString = () => JSON.stringify({
    name,
    hourlyRate,
    role,
    selectedSalaryType,
  });
  this.initial = this.toString();
}

/**
 * @description Настройки схемы расчета ЗП Руководителя.
 * @param {string} name
 * @param {string} role
 * @param {number} pieceworkDefaultPercentage
 * @param {number} salaryRateRoubles
 * @param {array}  selectedExclusions
 * @param {string} selectedSalaryType
 * @param {boolean} clinicManagerPieceworkWithComplaint
 * @param {boolean} clinicManagerPieceworkWithoutDiscounts
 * @param {boolean} clinicManagerPieceworkPatientExclusions
 * @param {array}  selectedPatientExclusions
 * @constructor
 */
export function ClinicManagerPayrollSettings(
  name = '',
  role = '',
  pieceworkDefaultPercentage = 0,
  salaryRateRoubles = 0,
  selectedExclusions = [],
  selectedSalaryType = '',
  clinicManagerPieceworkWithComplaint = false,
  clinicManagerPieceworkWithoutDiscounts = false,
  clinicManagerPieceworkPatientExclusions = false,
  selectedPatientExclusions = [],
) {
  this.toString = () => JSON.stringify({
    name,
    role,
    pieceworkDefaultPercentage,
    salaryRateRoubles,
    selectedExclusions,
    selectedSalaryType,
    clinicManagerPieceworkWithComplaint,
    clinicManagerPieceworkWithoutDiscounts,
    clinicManagerPieceworkPatientExclusions,
    selectedPatientExclusions,
  });
  this.initial = this.toString();
  this.getSelectedExclusions = () => selectedExclusions;
}

/**
 * @description Настройки схемы расчета ЗП Врача.
 * @param {string} name
 * @param {string} role
 * @param {number} pieceworkDefaultPercentage
 * @param {number} salaryRateRoubles
 * @param {array}  selectedExclusions
 * @param {string} selectedSalaryType
 * @param {boolean} doctorPieceworkWithComplaint
 * @param {boolean} doctorPieceworkWithoutDiscounts
 * @param {boolean} doctorPieceworkPatientExclusions
 * @param {array}  selectedPatientExclusions
 * @constructor
 */
export function DoctorPayrollSettings(
  name = '',
  role = '',
  pieceworkDefaultPercentage = 0,
  salaryRateRoubles = 0,
  selectedExclusions = [],
  selectedSalaryType = '',
  doctorPieceworkWithComplaint = false,
  doctorPieceworkWithoutDiscounts = false,
  doctorPieceworkPatientExclusions = false,
  selectedPatientExclusions = [],
) {
  this.toString = () => JSON.stringify({
    name,
    role,
    pieceworkDefaultPercentage,
    salaryRateRoubles,
    selectedExclusions,
    selectedSalaryType,
    doctorPieceworkWithComplaint,
    doctorPieceworkWithoutDiscounts,
    doctorPieceworkPatientExclusions,
    selectedPatientExclusions,
  });
  this.initial = this.toString();
  this.getSelectedExclusions = () => selectedExclusions;
}

/**
 * @description Настройки схемы расчета ЗП Ассистента.
 * @param {string} name
 * @param {string} role
 * @param {number} salaryRateRoubles
 * @param {array}  selectedExclusions
 * @param {string} selectedSalaryType
 * @constructor
 */
export function AssistantPayrollSettings(
  name = '',
  role = '',
  salaryRateRoubles = 0,
  selectedExclusions = [],
  selectedSalaryType = '',
) {
  this.toString = () => JSON.stringify({
    name,
    role,
    salaryRateRoubles,
    selectedExclusions,
    selectedSalaryType,
  });
  this.initial = this.toString();
}

/**
 * @description Шлюз расчета заработной платы.
 * @param {Axios} axios HTTP клиент.
 * @constructor
 */
export default function PayrollGateway(axios) {
  /**
   * @description Начислить расчитанную заработную плату.
   * @param {string} calculationId Идентификатор расчета.
   * @returns {Promise<Either>}
   */
  this.accrueCalculation = (calculationId) => axios
    .put(`/api/employee-payroll-calculations/${calculationId}/accrued`)
    .then(
      (response) => Either.of(response.data),
      (error) => {
        if (error.isAxiosError && [400, 403].includes(error.response.status)) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Выгрузить расчет заработной платы в Excel таблицу.
   * @param {string} calculationId Идентификатор расчета.
   * @returns {Promise<Either>}
   */
  this.downloadEmployeesPayrollExcelSpreadsheet = (calculationId) => axios
    .get(
      `/api/employee-payroll-calculations/${calculationId}`,
      {
        headers: { accept: 'application/vnd.ms-excel' },
        responseType: 'blob',
      },
    )
    .then(
      async (response) => Either.Right(response.data),
      async (error) => {
        if (error.isAxiosError && error.response.status === 400) {
          const { data: blob } = error.response;
          if (blob.type === 'application/json') {
            const data = JSON.parse(await blob.text());
            return Either.Left(data);
          }
        }
        throw error;
      },
    );

  /**
   * @description Изменить настройки расчета заработной платы администратора.
   * @param {string} employeeId Идентификатор сотрудника.
   * @param {string} hourlyRate Сумма почасовой ставки.
   * @returns {Promise<Either>}
   */
  this.editAdminPayrollSettings = (employeeId, salaryRate, salaryType) => axios
    .put(
      `/api/administrator-payroll-settings/${employeeId}`,
      {
        salary_rate_roubles: salaryRate,
        salary_rate_type: salaryType,
      },
    )
    .then(
      (response) => Either.of(response.data),
      (error) => {
        if (error.isAxiosError && [400, 403].includes(error.response.status)) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Изменить настройки расчета заработной платы ассистента.
   * @param {string}   employeeId                          Идентификатор сотрудника - ассистента.
   * @param {object[]} pieceworkExclusions                 Исключения сдельной части.
   * @param {string[]} removedPieceworkExclusions          Удаленные исключения сдельной части.
   * @param {string}   salaryRateRoubles                   Сумма ставки оклада.
   * @param {string}   salaryRateType                      Тип ставки оклада.
   * @param {object[]} assistantSelectedCategory           Категории услуг сдельной части.
   * @param {object[]} assistantSalariesByAssistingDoctors Оклады при ассистировании.
   * @param {object[]} removedSalariesByAssistingDoctorIds
   *        ID докторов на исключение окладов по ассистированию.
   * @returns {Either}
   */
  this.editAssistantPayrollSettings = (
    employeeId,
    pieceworkExclusions,
    removedPieceworkExclusions,
    salaryRateRoubles,
    salaryRateType,
    assistantSelectedCategory,
    assistantSalariesByAssistingDoctors,
    removedSalariesByAssistingDoctorIds,
  ) => axios
    .put(`/api/assistant-payroll-settings/${employeeId}`, {
      piecework_exclusions: pieceworkExclusions.map((item) => ({
        is_assigned_manually: item.isAssignedManually,
        service_id: item.value,
        percentage: item.percentage,
        fixed_price: item.fixed,
      })),
      piecework_removed_exclusion_ids: removedPieceworkExclusions,
      salary_rate_roubles: salaryRateRoubles,
      salary_rate_type: salaryRateType,
      piecework_service_category: assistantSelectedCategory.map((item) => ({
        category_id: item.value,
        percentage: item.percentage,
        fixed_price: item.fixed,
      })),
      salaries_by_assisting_doctors: assistantSalariesByAssistingDoctors,
      removed_salaries_by_assisting_doctor_ids: removedSalariesByAssistingDoctorIds,
    })
    .then(
      (response) => Either.of(response.data),
      (error) => {
        if (error.isAxiosError && [400, 403].includes(error.response.status)) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Изменить настройки расчета заработной платы врача.
   * @param {string}   employeeId                      Идентификатор сотрудника - врача.
   * @param {string}   pieceworkDefault                Процент сдельной части по умолчанию.
   * @param {object[]} pieceworkExclusions             Исключения сдельной части.
   * @param {string[]} removedPieceworkExclusions      Удаленные исключения сдельной части.
   * @param {string}   salaryRateRoubles               Сумма ставки оклада.
   * @param {string}   salaryRateType                  Тип ставки оклада.
   * @param {object[]} doctorSelectedCategory          Категории услуг сдельной части.
   * @param {object[]} doctorPieceworkWithComplaint    Учет по рекламации.
   * @param {object[]} doctorPieceworkWithoutDiscounts Учет по скидкам.
   * @param {string[]} doctorPatientExclusions         Исключения сдельной части пациентов.
   * @returns {Either}
   */
  this.editDoctorPayrollSettings = (
    employeeId,
    pieceworkDefault,
    pieceworkExclusions,
    removedPieceworkExclusions,
    salaryRateRoubles,
    salaryRateType,
    doctorSelectedCategory,
    doctorPieceworkWithComplaint,
    doctorPieceworkWithoutDiscounts,
    doctorPatientExclusions,
  ) => axios
    .put(`/api/doctor-payroll-settings/${employeeId}`, {
      piecework_default: pieceworkDefault,
      piecework_exclusions: pieceworkExclusions.map((item) => ({
        is_assigned_manually: item.isAssignedManually,
        service_id: item.value,
        percentage: item.percentage,
        fixed_price: item.fixed,
      })),
      piecework_removed_exclusion_ids: removedPieceworkExclusions,
      salary_rate_roubles: salaryRateRoubles,
      salary_rate_type: salaryRateType,
      piecework_service_category: doctorSelectedCategory.map((item) => ({
        category_id: item.value,
        percentage: item.percentage,
        fixed_price: item.fixed,
      })),
      piecework_with_complaint: doctorPieceworkWithComplaint,
      piecework_without_discounts: doctorPieceworkWithoutDiscounts,
      piecework_patient_exclusion_ids: doctorPatientExclusions,
    })
    .then(
      (response) => Either.of(response.data),
      (error) => {
        if (error.isAxiosError && [400, 403].includes(error.response.status)) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Изменить настройки расчета заработной платы врача.
   * @param {string}   employeeId                      Идентификатор сотрудника - врача.
   * @param {string}   pieceworkDefault                Процент сдельной части по умолчанию.
   * @param {object[]} pieceworkExclusions             Исключения сдельной части.
   * @param {string[]} removedPieceworkExclusions      Удаленные исключения сдельной части.
   * @param {string}   salaryRateRoubles               Сумма ставки оклада.
   * @param {string}   salaryRateType                  Тип ставки оклада.
   * @param {object[]} clinicManagerSelectedCategory          Категории услуг сдельной части.
   * @param {object[]} clinicManagerPieceworkWithComplaint    Учет по рекламации.
   * @param {object[]} clinicManagerPieceworkWithoutDiscounts Учет по скидкам.
   * @param {string[]} clinicManagerPatientExclusions         Исключения сдельной части пациентов.
   * @returns {Either}
   */
  this.editClinicManagerPayrollSettings = (
    employeeId,
    pieceworkDefault,
    pieceworkExclusions,
    removedPieceworkExclusions,
    salaryRateRoubles,
    salaryRateType,
    clinicManagerSelectedCategory,
    clinicManagerPieceworkWithComplaint,
    clinicManagerPieceworkWithoutDiscounts,
    clinicManagerPatientExclusions,
  ) => axios
    .put(`/api/clinic-manager-payroll-settings/${employeeId}`, {
      piecework_default: pieceworkDefault,
      piecework_exclusions: pieceworkExclusions.map((item) => ({
        is_assigned_manually: item.isAssignedManually,
        service_id: item.value,
        percentage: item.percentage,
        fixed_price: item.fixed,
      })),
      piecework_removed_exclusion_ids: removedPieceworkExclusions,
      salary_rate_roubles: salaryRateRoubles,
      salary_rate_type: salaryRateType,
      piecework_service_category: clinicManagerSelectedCategory.map((item) => ({
        category_id: item.value,
        percentage: item.percentage,
        fixed_price: item.fixed,
      })),
      piecework_with_complaint: clinicManagerPieceworkWithComplaint,
      piecework_without_discounts: clinicManagerPieceworkWithoutDiscounts,
      piecework_patient_exclusion_ids: clinicManagerPatientExclusions,
    })
    .then(
      (response) => Either.of(response.data),
      (error) => {
        if (error.isAxiosError && [400, 403].includes(error.response.status)) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Сформировать расчет заработной платы.
   * @param {string[]} employeeIds    Идентификаторы сотрудников.
   * @param {string}   dateFrom       Дата начала.
   * @param {string}   dateTo         Дата окончания.
   * @param {string}   calculatedType Тип расчета.
   * @returns {Promise<Maybe>}
   */
  this.formPayroll = (
    employeeIds,
    dateFrom,
    dateTo,
    calculatedType,
  ) => axios
    .post(
      `/api/employee-payroll/${dateFrom}:${dateTo}`,
      {
        employee_ids: employeeIds,
        calculated_type: calculatedType,
      },
    )
    .then((response) => Maybe.Some(response.data));

  /**
   * @description Показать настройки расчета администратора или роли клиники.
   * @param {string} employeeId Идентификатор сотрудника.
   * @returns {Promise<Either>}
   */
  this.viewAdminPayrollSettings = (employeeId) => axios
    .get(`/api/administrator-payroll-settings/${employeeId}`)
    .then(
      (response) => Either.of(response.data),
      (error) => {
        if (error.isAxiosError && [400, 403].includes(error.response.status)) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Показать настройки расчета ассистента.
   * @param {string} employeeId              Идентификатор сотрудника.
   * @param {string|null} servicePriceListId Идентификатор прайс-листа
   * @param {boolean} byDefaultPriceList     Поиск по прайс-листу по умолчанию
   * @returns {Promise<Either>}
   */
  this.viewAssistantPayrollSettings = (
    employeeId,
    servicePriceListId = null,
    byDefaultPriceList = false,
  ) => axios
    .get(`/api/assistant-payroll-settings/${employeeId}`, {
      params: {
        service_price_list_id: servicePriceListId,
        by_default_service_price_list: byDefaultPriceList,
      },
    })
    .then(
      (response) => Either.of(response.data),
      (error) => {
        if (error.isAxiosError && [400, 403].includes(error.response.status)) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Показать настройки расчета врача.
   * @param {string} employeeId          Идентификатор сотрудника.
   * @param {string} servicePriceListId  Идентификатор прайс-листа.
   * @param {boolean} byDefaultPriceList Поиск по прайс-листу по умолчанию
   * @returns {Promise<Either>}
   */
  this.viewDoctorPayrollSettings = (
    employeeId,
    servicePriceListId = null,
    byDefaultPriceList = false,
  ) => axios
    .get(`/api/doctor-payroll-settings/${employeeId}`, {
      params:
        {
          service_price_list_id: servicePriceListId,
          by_default_service_price_list: byDefaultPriceList,
        },
    })
    .then(
      (response) => Either.of(response.data),
      (error) => {
        if (error.isAxiosError && [400, 403].includes(error.response.status)) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Показать настройки расчета Руководителя.
   * @param {string} employeeId          Идентификатор сотрудника.
   * @param {string} servicePriceListId  Идентификатор прайс-листа.
   * @param {boolean} byDefaultPriceList Поиск по прайс-листу по умолчанию
   * @returns {Promise<Either>}
   */
  this.viewClinicManagerPayrollSettings = (
    employeeId,
    servicePriceListId = null,
    byDefaultPriceList = false,
  ) => axios
    .get(`/api/clinic-manager-payroll-settings/${employeeId}`, {
      params:
        {
          service_price_list_id: servicePriceListId,
          by_default_service_price_list: byDefaultPriceList,
        },
    })
    .then(
      (response) => Either.of(response.data),
      (error) => {
        if (error.isAxiosError && [400, 403].includes(error.response.status)) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Показать опции расчета.
   * @returns {Promise<Maybe>}
   */
  this.viewOptions = () => axios
    .get('/api/payroll-options')
    .then(async (response) => Maybe.Some(response.data));

  /**
   * @description Показать отчет по расчетам с сотрудником.
   * @param {string}   dateFrom  Начальная дата начислений.
   * @param {string}   dateTo    Конечная дата начислений.
   * @param {string[]} employees Идентификаторы получаетелей.
   * @returns {Promise<Maybe>}
   */
  this.viewReport = (dateFrom, dateTo, employees) => axios
    .get(
      '/api/employee-reports/hourly-payroll',
      {
        params: {
          accrual_from: dateFrom,
          accrual_to: dateTo,
          recipient_ids: employees,
        },
      },
    )
    .then((response) => Maybe.of(response.data));

  /**
   * @description Показать опции фильтров отчета по расчетам с сотрудником.
   * @returns {Promise<Maybe>}
   */
  this.viewReportOptions = () => axios
    .get('/api/report-filters/employees-payroll')
    .then((response) => Maybe.of(response.data));

  /**
   * @description Показать детализацию по расчету сотрудника.
   * @param {string} calculationId Идентификатор расчета.
   * @param {string} employeeId    Идентификатор сотрудника.
   * @returns {Promise<Maybe>}
   */
  this.viewSlipDetails = (calculationId, employeeId) => axios
    .get(`/api/employee-payroll-calculations/${calculationId}/employees/${employeeId}`)
    .then((response) => Maybe.of(response.data));
}
